import React, { useState } from 'react';
import { Redirect } from 'react-router';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { collaborationProviderProfileRoute, ROUTING_CONFIG } from '@uplift/constants';
import { Chip, Loader, ProviderLayout, ScrollableView } from '@uplift/ui-v2';
import { useCollaborativeProviderDetailsQuery } from '@uplift/queries';

import { ActivityFeed } from './components/ActivityFeed';
import { SupervisionMeetingsStats } from './components/SuperviseeDetails';
import { SupervisionMeetingDetailModal } from './components/SupervisionMeetingDetailModal.component';
import { ActivitiesActionMenu } from './components/ActivitiesActionMenu';
import { SuperviseeProviderProfileGeneralInfo } from './components/SuperviseeDetails/SuperviseeProviderProfileGeneralInfo.component';
import { StateLabel } from '../../components/StatesLabel';


export const SuperviseeDetailsPageContent: React.VFC = () => {
  const { superviseeId } = collaborationProviderProfileRoute.useParams();
  const { data: superviseeDetails } = useCollaborativeProviderDetailsQuery(superviseeId);
  const [ selectedActivityId, setSelectedActivityId ] = useState<string | null>(null);
  const [ modalOpened, setModalOpened ] = useState<'meetingDetails' | null>(null);
  const [ filterByStateId, setFilterByStateId ] = useState<string | null>(null);

  if (!superviseeDetails) {
    return <Redirect to={ROUTING_CONFIG.collaborationManage} />;
  }

  return (
    <>
      <Box sx={{ display: 'flex', flex: 1 }}>
        <Box sx={{ width: 420, borderRight: 1, borderColor: 'divider', backgroundColor: 'background.paper' }}>
          <SuperviseeProviderProfileGeneralInfo providerId={superviseeId} />
          <Divider />
          <SupervisionMeetingsStats providerId={superviseeId} />
        </Box>
        <ScrollableView>
          <Box sx={{ padding: '48px 40px', display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h5" fontWeight={450}>Activity</Typography>
              <ActivitiesActionMenu superviseeId={superviseeId} />
            </Box>
            <Stack direction="row" gap={1} alignItems="center">
              <Typography>Filter by state</Typography>
              <Chip
                clickable
                onClick={() => setFilterByStateId(null)}
                label="All states"
                variant={filterByStateId === null ? 'filled' : 'outlined'}
                color="primary"
              />
              {superviseeDetails.collaborativeStateIds.map(stateId => {
                const selected = filterByStateId === stateId;

                return (
                  <Chip
                    key={stateId}
                    label={<StateLabel stateId={stateId} />}
                    variant={selected ? 'filled' : 'outlined'}
                    color="primary"
                    clickable
                    onClick={() => setFilterByStateId(stateId)}
                  />
                );
              })}
            </Stack>
            <React.Suspense fallback={<Loader height="200px" />}>
              <ActivityFeed
                providerId={superviseeId}
                stateId={filterByStateId}
                onSelectActivity={activityId => {
                  setSelectedActivityId(activityId);
                  setModalOpened('meetingDetails');
                }}
              />
            </React.Suspense>
          </Box>
        </ScrollableView>
      </Box>
      {selectedActivityId && (
        <SupervisionMeetingDetailModal
          open={modalOpened === 'meetingDetails'}
          onClose={() => setModalOpened(null)}
          onClosed={() => setSelectedActivityId(null)}
          meetingId={selectedActivityId}
          providerId={superviseeId}
        />
      )}
    </>
  );
};

export const SuperviseeDetailsPage: React.VFC = () => {
  return (
    <ProviderLayout bodyScroll={false}>
      <SuperviseeDetailsPageContent />
    </ProviderLayout>
  );
};
